import React, { Component } from "react";
import {
    Grid, Button, CircularProgress, TextField, Dialog, DialogContent, DialogActions, DialogTitle,
    LinearProgress, Collapse, Typography, Box, IconButton, Paper, Breadcrumbs, Snackbar, Alert
} from "@mui/material";
import TierDropdownPill from "../tierDropdownPill";
import Dropzone from "../Dropzone";
import SomethingWrong from "../../media/somethingwrong.jpg";
import { confirm } from "../confirmation";
import { toast } from "react-toastify";
import { Img } from "react-image";
import History from "../../utils/history";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faDownload,
    faExclamationTriangle,
    faTrash,
    faPlay,
} from "@fortawesome/free-solid-svg-icons";
import CloseIcon from '@mui/icons-material/Close';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Moment from "moment";
import HelpHint from "../parts/helpHint";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API_URL, UPLOADS_BUCKET, TEMP_BUCKET } from "../../config";
import { S3Client, GetObjectCommand, PutObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import ImagePreviewModal from "../parts/ImagePreviewModal";

import { CollectionsApi } from "../../services/api/api";
import UsersApi from '../../services/api/users';
import CollectionFolderMoveDialog from './collectionFolderMoveDialog';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';

// Video extensions supported by the system
const VIDEO_EXTENSIONS = new Set(['.mp4', '.mov', '.avi', '.webm', '.mkv']);

// Helper function to check if a file is a video
const isVideoFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return VIDEO_EXTENSIONS.has(extension);
};

// Helper function to normalize filename with lowercase extension
const normalizeFileName = (fileName) => {
    if (!fileName) return fileName;
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return fileName; // No extension

    const nameWithoutExt = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex).toLowerCase();
    return nameWithoutExt + extension;
};

function SortableMedia(props) {
    const { sortMode } = props;

    // Only use sortable hooks when in sort mode
    const sortableProps = sortMode ? useSortable({ id: props.id }) : {
        attributes: {},
        listeners: {},
        setNodeRef: () => { },
        transform: null,
        transition: null,
    };

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = sortableProps;

    const style = {
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition,
    };

    const isProcessing = props.data.status === "PROCESSING" || props.data.status === "UPLOADED";
    const isFailed = props.data.status === "FAILED";
    const isSuccessNoOptimization = props.data.status === "SUCCESS_NO_OPTIMIZATION";
    const isSuccess = props.data.status === "SUCCESS";
    const isVideo = isVideoFile(props.data.f);

    // Allow clicking for SUCCESS, SUCCESS_NO_OPTIMIZATION, and PROCESSING states
    const isClickable = isSuccess || isSuccessNoOptimization || isProcessing || isFailed;

    return (
        <Grid
            item
            ref={setNodeRef}
            style={style}
            {...(sortMode ? attributes : {})}
            {...(sortMode ? listeners : {})}
            lg={3}
            md={4}
            sm={6}
            xs={6}
            className="p-1 m-0 rounded no-overflow col-thumb"
        >
            <Box
                sx={{
                    width: '100%',
                    paddingBottom: '100%', // Creates a square aspect ratio
                    position: 'relative',  // Contain absolutely positioned image
                    overflow: 'hidden',    // Prevent image from spilling out
                    cursor: (!sortMode && isClickable) ? 'pointer' : 'default'
                }}
                onClick={sortMode ? undefined : (isClickable ? props.onClick : undefined)}
            >
                <Img
                    src={props.data.src || SomethingWrong}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                    loader={
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: 'action.hover',
                            }}
                        >
                            <CircularProgress color="primary" />
                        </Box>
                    }
                    className="img-thumbnail"
                />
                {(isProcessing || isFailed || isSuccessNoOptimization) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: isFailed
                                ? 'linear-gradient(to top, rgba(220,20,60,0.9), transparent)'
                                : isSuccessNoOptimization
                                    ? 'linear-gradient(to top, rgba(255,140,0,0.8), transparent)'
                                    : 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                            color: 'white',
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            textAlign: 'center',
                        }}
                    >
                        {isFailed && 'Processing Failed'}
                        {isProcessing && (isVideo ? 'Video Processing...' : 'Processing...')}
                        {isSuccessNoOptimization && (isVideo ? 'Video Ready (Basic)' : 'Ready')}
                    </Box>
                )}
                {isVideo && isSuccess && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            fontSize: '2rem',
                            textShadow: '0 0 4px rgba(0,0,0,0.8)',
                            pointerEvents: 'none',
                        }}
                    >
                        <FontAwesomeIcon icon={faPlay} />
                    </Box>
                )}
            </Box>
        </Grid>
    );
}

class CollectionDetails extends Component {
    _isMounted = false;
    _fileNos = 0;
    _fileNosPeak = 0;
    intervalID = 0;
    pickAMonth = React.createRef();

    initGetMedia = false;

    state = {
        redirected: false,
        id: this.props.match.params.id,
        image: "",
        file: null,
        message: "",
        isUploading: false,
        user: this.props.user,
        loading: true,
        mediaLoaded: false,  // Track if media have been loaded
        collectionId: "",
        collectionOwnerId: "",
        copyright: "",
        createdAt: "",
        notes: "",
        preload: true,
        mediaCount: 0,
        filesToSend: 0,
        percent: 0,
        collapseProgress: false,
        init: true,
        isLoading: true,
        media: [
            // url, id, redirect
        ],
        loadedMedia: [],
        fullMedia: [],
        currentFullMedia: "",
        AWS: this.props.AWS,
        editNote: false,
        editCopyright: false,
        oldCopyright: "",
        oldNotes: "",
        oldName: "",
        setStatus: "",
        isRequesting: false,
        isStatusSuccess: false,
        allowDownload: false,
        disableCopyright: false,
        disableNotes: false,
        disableName: false,
        editName: false,
        showModal: false,
        currentImageIndex: null,
        editTier: false,
        statusColor: "#DDDDDD",
        sortMode: false,
        sortDesc: "Sort Media",
        expectedMediaCount: 0, // Track expected total media
        successMediaCount: 0,  // Track successfully processed media
        processingMediaCount: 0, // Track media being processed
        failedMediaCount: 0,   // Track failed media
        emptyMediaCount: 0,    // Track media with no status
        statusCheckAttempts: 0, // Track how many times we've checked status
        maxStatusCheckAttempts: 120, // Max attempts before giving up (10 minutes at 5s intervals)
        lastUploadCount: 0,    // Track media before upload for comparison
        baseMediaCount: 0,     // Base count before any uploads in this session
        totalExpectedCount: 0, // Total expected after uploads (base + uploads)
        // SS-470: which mediaIds have already shown the per-poll error toast
        // this session. Prevents the toast from re-firing every 5 seconds.
        _shownErrorToastIds: new Set(),
        // SS-470: which mediaIds have already been auto-redriven this session.
        // Cap at 1 attempt per mediaId.
        _autoRedrivenIds: new Set(),
        // SS-470: epoch-seconds watermark for delta polling.
        _lastStatusPollAt: 0,
        activeMonth: Moment().unix(),
        monthValue: new Date(), // Initialize with current date
        dropRef: null,
        dragging: false,
        disableDragging: false,
        prevItem: null,
        nextItem: null,
        nextIndex: null,
        prevIndex: null,
        versionTime: 1591213166,
        notSubscribed: false,
        monthlyEnabled: null,
        isMoveDialogOpen: false,
        folderPath: [],
        allCollections: [],
        snackbar: { open: false, message: '', severity: 'success' },
    };

    deb = 0;
    drag_timeout = null;
    debounce_timeout = null;
    dropped_something = false;

    setDragging(i, force = false) {
        if (force) {
            this.setState({ dragging: i });
            return;
        }
        if (this.deb <= 0) {
            this.setState({ dragging: i });
            this.deb = 3;
        } else {
            this.deb--;
        }

        if (!force) {
            this.debounce();
        }

        if (!force) {
            this.drag_timeout = setTimeout(() => {
                this.setState({ dragging: false });
            }, 2000);
        }
    }

    debounce() {
        clearTimeout(this.debounce_timeout);
        this.debounce_timeout = setTimeout(() => {
            this.setState({ dragging: false });
        }, 100);
    }

    constructor(props) {
        super(props);
        this.escFunction = this.escFunction.bind(this);
    }

    getToken = async () => {
        let token = this.props.token;
        if (typeof token === 'function') {
            token = await token();
        }
        this.setState({ token: token });
    };

    openModal = (i, id, index, mediaStatus, prev, next) => {
        console.log("Opening modal:", { i, id, index, mediaStatus });
        if (mediaStatus === "SUCCESS" || mediaStatus === "PROCESSING" || mediaStatus === "SUCCESS_NO_OPTIMIZATION") {
            this.getImage(i, id, this.truncate(this.state.media[index].f, 40));
            this.setState({
                showModal: true,
                currentImage: i,
                currentImageId: id,
                currentImageIndex: index,
                currentImageStatus: mediaStatus,
            });
        } else if (mediaStatus === "FAILED") {
            // SS-461: open the modal so the user sees Redrive + Delete recovery
            // controls in the ImagePreviewModal recovery panel. The modal's
            // own useEffect detects FAILED + sets recoveryMode = true.
            this.setState({
                showModal: true,
                currentImage: i,
                currentImageId: id,
                currentImageIndex: index,
                currentImageStatus: mediaStatus,
            });
        } else {
            const statusMessage = "This media is still processing";
            toast.warn(statusMessage);
        }
    };

    truncate = (str, n) => {
        return str.length > n ? str.substr(0, n - 1) + "..." : str;
    };

    closeModal = () => {
        console.log("Closing modal");
        if (this._isMounted) {
            this.setState({
                showModal: false,
                currentImageIndex: null
            });
        }
    };

    getImage = async (key, id, fileName) => {
        this.setState({ currentFullMedia: "" });
        if (!this._isMounted) return;
        try {
            // Check if credentials are available
            if (!this.props.AWS?.config?.credentials) {
                console.error("AWS credentials are missing for getImage");
                return;
            }

            const s3 = new S3Client({
                region: "us-east-1",
                credentials: this.props.AWS.config.credentials,
            });

            let newKey =
                "collections/" +
                this.state.collectionOwnerId +
                "/" +
                this.state.collectionId +
                "/optimized/" +
                fileName.replace(/\.[^/.]+$/, "") + ".jpg";

            const command = new GetObjectCommand({
                Bucket: UPLOADS_BUCKET,
                Key: newKey,
            });

            const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
            if (this._isMounted) {
                this.setState({ currentFullMedia: url });
            }
        } catch (err) {
            toast.error("Error getting image: " + err);
        }
    };

    escFunction(event) {
        if (event.keyCode === 27) {
            this.closeModal();
        }
    }

    incrementFileNos = async () => {
        // Don't increment, this is just to track that we started a file
        // The actual progress is tracked by decrementFileNos
    };

    decrementFileNos = async (ev = null) => {
        this._fileNos++;
        const percent = Math.min((this._fileNos / this._fileNosPeak) * 100, 100);

        this.setState({ percent: percent });

        // If all files are uploaded, prepare for status monitoring
        if (this._fileNos >= this._fileNosPeak) {
            console.log(`Upload complete! ${this._fileNos} files uploaded. Starting processing monitoring...`);

            // Set status to "Processing" when all files are uploaded to S3
            await this.updateCollectionStatus("Processing");

            setTimeout(() => {
                this.setState({
                    percent: 100,
                    collapseProgress: false,
                    isUploading: false
                });

                toast.info("Upload complete! Media is being processed...");

                // The monitoring was already started in onDrop, so we don't need to start it again here

            }, 1000);
        }
    };

    collapseDelay() {
        this.setState({ percent: 0, collapseProgress: false });
    }

    refreshMedia = () => {
        // Reset the media loading flag and reload media
        this.initGetMedia = false;
        this.setState({ media: [] }); // Clear current media
        this.getMedia(); // Reload media
    };

    refreshMediaWithDelay = (delayMs = 5000) => {
        // Add a delay before refreshing to give media time to process
        setTimeout(() => {
            this.refreshMedia();
        }, delayMs);
    };

    // Check for processing media and refresh periodically until all are processed
    startProcessingMonitor = () => {
        const checkInterval = 5000; // Check every 5 seconds
        const maxChecks = 24; // Maximum 2 minutes of checking (24 * 5 seconds)
        let checkCount = 0;

        const processMonitor = setInterval(async () => {
            checkCount++;

            // Refresh media to check for processing status
            this.refreshMedia();

            // Wait a bit for state to update after refresh
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Check if all media are processed or we've reached max checks
            const hasProcessingMedia = this.state.media.some(m => m.status !== "SUCCESS");

            if (!hasProcessingMedia || checkCount >= maxChecks) {
                clearInterval(processMonitor);
                if (hasProcessingMedia) {
                    toast.info("Some media may still be processing. Please refresh manually if needed.");
                } else {
                    // All media is processed successfully - set status to Ready for Distribution
                    console.log("All media processed successfully. Setting status to Ready for Distribution.");
                    await this.updateCollectionStatus("Ready for Distribution");
                    toast.success("All media processed successfully! Set is now ready for distribution.");
                }
            }
        }, checkInterval);

        // Store the interval ID so we can clear it if component unmounts
        this.processingMonitorId = processMonitor;
    };

    getUploadableFiles = (files) => {
        const blocked = [];
        const uploadable = [];

        files.forEach((file) => {
            const lower = (file?.name || '').toLowerCase();
            if (lower.endsWith('.gif') || file?.type === 'image/gif') {
                blocked.push(file.name);
                return;
            }
            uploadable.push(file);
        });

        if (blocked.length > 0) {
            toast.warn(`GIF uploads are not supported yet. Skipped: ${blocked.slice(0, 3).join(', ')}${blocked.length > 3 ? ` and ${blocked.length - 3} more` : ''}`);
        }

        return uploadable;
    };

    uploadFile = async (filesOrEvent, i) => {
        const files = Array.isArray(filesOrEvent)
            ? filesOrEvent
            : Array.from(filesOrEvent?.target?.files || []);
        if (!files.length || i >= files.length) {
            return;
        }

        console.log("upload #" + i + " starting");

        // Check if credentials are available
        if (!this.props.AWS?.config?.credentials) {
            console.error("AWS credentials are missing");
            toast.error("AWS credentials not available. Please try refreshing the page.");
            this.decrementFileNos();
            return;
        }

        const s3 = new S3Client({
            region: "us-east-1",
            credentials: this.props.AWS.config.credentials,
        });
        let file = files[i];
        let name = normalizeFileName(files[i].name);

        try {
            // SS-470: Call addMedia FIRST to check for dedupe before S3 PUT.
            // If alreadyExists, skip upload. If unique fileName returned, use it.
            const addResponse = await this.addMediaEndpoint(name, file.size, file.type);

            if (addResponse?.alreadyExists) {
                toast.info(`"${name}" was already uploaded successfully — linked existing.`);
                // SS-470: Decrement expected count since this file was deduped
                // (linked to existing) and doesn't represent a new upload.
                this.setState((prev) => ({
                    totalExpectedCount: Math.max(0, prev.totalExpectedCount - 1),
                    expectedMediaCount: Math.max(0, prev.expectedMediaCount - 1),
                }));
                this.decrementFileNos();
                const nextIndex = i + 1;
                if (nextIndex < files.length) {
                    this.uploadFile(files, nextIndex);
                }
                return;
            }

            // Use unique fileName if backend generated one
            const uploadName = addResponse?.fileName || name;

            let key =
                "collections/" +
                this.state.collectionOwnerId +
                "/" +
                this.state.collectionId +
                "/" +
                uploadName;

            // Convert file to ArrayBuffer to avoid stream reader issues
            const fileBuffer = await file.arrayBuffer();

            const command = new PutObjectCommand({
                Bucket: TEMP_BUCKET,
                Key: key,
                Body: new Uint8Array(fileBuffer),
                ContentType: file.type,
            });

            await s3.send(command);

            this.decrementFileNos();

            // Process next file
            const nextIndex = i + 1;
            if (nextIndex < files.length) {
                this.uploadFile(files, nextIndex);
            }
        } catch (err) {
            this.decrementFileNos();
            console.error("Upload error:", err);
            toast.error("Upload failed: " + err.message);

            // Continue with next file even if one fails
            const nextIndex = i + 1;
            if (nextIndex < files.length) {
                this.uploadFile(files, nextIndex);
            }
        }
    };

    onChange = async (ev) => {
        ev.persist();

        if (this._isMounted) {
            this.setState({ allowDownload: false, isStatusSuccess: false });
        }

        const selectedFiles = this.getUploadableFiles(Array.from(ev.target.files || []));
        let fileLength = selectedFiles.length;
        if (fileLength === 0) {
            return;
        }
        this._fileNosPeak = fileLength;
        this._fileNos = 0; // Reset completed files counter

        console.log(`Starting file input upload of ${fileLength} files`);
        console.log(`Current media counts: Media=${this.state.media.length}, Total expected before upload=${this.state.totalExpectedCount}`);

        // SS-493: Don't optimistically increment totalExpectedCount.
        // Count is updated per-file after successful addMediaEndpoint.
        this.setState({
            filesToSend: fileLength,
            isUploading: true,
            collapseProgress: true,
            percent: 0,
        });

        this.uploadFile(selectedFiles, 0);

        // Start post-upload monitoring after a brief delay
        setTimeout(() => {
            this.startPostUploadMonitoring(fileLength);
        }, 2000); // Give time for upload to start
    };

    onDropUpload = async (files, i) => {
        console.log("upload #" + i + " starting");

        // Check if credentials are available
        if (!this.props.AWS?.config?.credentials) {
            console.error("AWS credentials are missing");
            toast.error("AWS credentials not available. Please try refreshing the page.");
            this.decrementFileNos();
            return;
        }

        const s3 = new S3Client({
            region: "us-east-1",
            credentials: this.props.AWS.config.credentials,
        });
        let file = files[i];
        let name = normalizeFileName(files[i].name);

        try {
            // SS-470: Call addMedia FIRST to check for dedupe before S3 PUT.
            const addResponse = await this.addMediaEndpoint(name, file.size, file.type);

            if (addResponse?.alreadyExists) {
                toast.info(`"${name}" was already uploaded successfully — linked existing.`);
                // SS-470: Decrement expected count since this file was deduped
                this.setState((prev) => ({
                    totalExpectedCount: Math.max(0, prev.totalExpectedCount - 1),
                    expectedMediaCount: Math.max(0, prev.expectedMediaCount - 1),
                }));
                this.decrementFileNos();
                const nextIndex = i + 1;
                if (nextIndex < files.length) {
                    this.onDropUpload(files, nextIndex);
                }
                return;
            }

            // SS-493: Increment count after successful addMediaEndpoint
            this.setState((prev) => ({
                totalExpectedCount: prev.totalExpectedCount + 1,
                expectedMediaCount: prev.expectedMediaCount + 1,
            }));

            const uploadName = addResponse?.fileName || name;

            let key =
                "collections/" +
                this.state.user.sub +
                "/" +
                this.state.collectionId +
                "/" +
                uploadName;

            // Convert file to ArrayBuffer to avoid stream reader issues
            const fileBuffer = await file.arrayBuffer();

            const command = new PutObjectCommand({
                Bucket: TEMP_BUCKET,
                Key: key,
                Body: new Uint8Array(fileBuffer),
                ContentType: file.type,
            });

            await s3.send(command);

            this.decrementFileNos();

            // Process next file
            const nextIndex = i + 1;
            if (nextIndex < files.length) {
                this.onDropUpload(files, nextIndex);
            }
        } catch (err) {
            this.decrementFileNos();
            console.error("Upload error:", err);
            toast.error("Upload failed: " + err.message);

            // Continue with next file even if one fails
            const nextIndex = i + 1;
            if (nextIndex < files.length) {
                this.onDropUpload(files, nextIndex);
            }
        }
    };

    onDrop = async (files) => {
        this.dropped_something = true;
        const uploadableFiles = this.getUploadableFiles(Array.from(files || []));
        let fileLength = uploadableFiles.length;
        if (fileLength === 0) {
            return;
        }

        this._fileNosPeak = fileLength;
        this._fileNos = 0; // Reset completed files counter

        console.log(`Starting upload of ${fileLength} files`);
        console.log(`Current media counts: Media=${this.state.media.length}, Total expected before upload=${this.state.totalExpectedCount}`);

        // SS-493: Don't optimistically increment totalExpectedCount.
        // Count is updated per-file after successful addMediaEndpoint.
        this.setState({
            filesToSend: fileLength,
            isUploading: true,
            collapseProgress: true,
            allowDownload: false,
            isStatusSuccess: false,
            dragging: false,
            disableDragging: true,
            percent: 0,
            lastUploadCount: fileLength,
        });

        // Start upload process
        this.onDropUpload(uploadableFiles, 0);

        // Start post-upload monitoring after a brief delay
        setTimeout(() => {
            this.startPostUploadMonitoring(fileLength);
        }, 2000); // Give time for upload to start
    };

    clickUpload() {
        document.getElementById("uploadButton").click();
    }

    componentDidMount() {
        this._isMounted = true;
        window.scrollTo(0, 0);
        // Warn on browser/tab close if uploading
        window.onbeforeunload = (e) => {
            if (this.state.isUploading) {
                return "Upload in progress. Are you sure you want to leave?";
            }
        };

        // Check for AWS credentials availability on mount
        if (!this.props.AWS?.config?.credentials) {
            console.warn("AWS credentials not available on component mount. Some features may not work properly.");
        }

        // Initialize status checking
        console.log("CollectionDetails mounted - initializing status check");

        // Check for cached data with user-specific key
        const userSpecificKey = `collection_${this.state.id}_${this.props.user?.sub || 'unknown'}`;
        if (localStorage.getItem(userSpecificKey)) {
            const itemLocalStorage = JSON.parse(localStorage.getItem(userSpecificKey));
            this.setState({
                collectionId: itemLocalStorage.collectionId,
                collectionName: itemLocalStorage.collectionName,
                tier: itemLocalStorage.tier,
                copyright: itemLocalStorage.copyright,
                notes: itemLocalStorage.notes,
                createdAt: itemLocalStorage.createdAt,
                setStatus: itemLocalStorage.setStatus,
                activeMonth: itemLocalStorage.activeMonth,
                monthValue: itemLocalStorage.monthValue,
                isStatusSuccess: itemLocalStorage.isActiveSuccess,
            });

            // Get collection details and media
            this.getCollectionDetails();
        } else {
            this.getCollectionDetails();
        }

        // Fetch monthly distribution setting
        const fetchMonthlySetting = async () => {
            try {
                let token = this.props.token;
                if (typeof token === 'function') {
                    token = await token();
                }
                if (!token || typeof token !== 'string' || token === 'undefined') {
                    return;
                }
                const res = await UsersApi.getMonthlyDistributionSetting(token);
                if (this._isMounted) {
                    const enabled = res?.data?.app_metadata?.monthlyDistributionEnabled === true;
                    this.setState({ monthlyEnabled: enabled });
                }
            } catch (err) {
                // Default to false on error
                if (this._isMounted) {
                    this.setState({ monthlyEnabled: false });
                }
            }
        };
        fetchMonthlySetting();

        // Fetch all collections for the move dialog folder list
        const fetchAllCollections = async () => {
            try {
                let token = this.props.token;
                if (typeof token === 'function') {
                    token = await token();
                }
                if (!token || typeof token !== 'string' || token === 'undefined') {
                    return;
                }
                const res = await CollectionsApi.getCollections(token);
                if (this._isMounted && res?.success && res?.data?.collectionlist) {
                    this.setState({ allCollections: res.data.collectionlist });
                }
            } catch (err) {
                // Non-critical — move dialog will just show Root + New folder
            }
        };
        fetchAllCollections();

        document.addEventListener("keydown", this.escFunction, false);

        // Initialize media area ref for drag and drop
        this.mediaAreaRef = React.createRef();
    }

    componentDidUpdate(prevProps, prevState) {
        // If user changed, clear any cached images and refresh data
        if (prevProps.user?.sub !== this.props.user?.sub) {
            console.log('User changed, clearing collection data and refreshing');
            this.setState({
                media: [],
                loadedMedia: [],
                fullMedia: [],
                mediaLoaded: false,
                loading: true,
            }, () => {
                // Refresh collection details for the new user context
                this.getCollectionDetails();
            });
            return; // Don't execute other checks if user changed
        }

        // Debug modal state changes
        if (prevState.showModal !== this.state.showModal || prevState.currentImageIndex !== this.state.currentImageIndex) {
            console.log("Modal state changed:", {
                showModal: this.state.showModal,
                currentImageIndex: this.state.currentImageIndex,
                imagesLength: this.state.media.length
            });
        }
    }

    disableMe = () => {
        this.setState({ dragging: false });
    };

    // New localized drag handlers for media area only
    handleMediaAreaDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.dragging && e.dataTransfer.items.length > 0) {
            // Check if dragging files
            const hasFiles = [...e.dataTransfer.items].some(item => item.kind === 'file');
            if (hasFiles) {
                this.setState({ dragging: true });
            }
        }
    };

    handleMediaAreaDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only hide overlay if leaving the media area entirely
        if (!e.currentTarget.contains(e.relatedTarget)) {
            this.setState({ dragging: false });
        }
    };

    handleMediaAreaDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    handleMediaAreaDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ dragging: false });

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            this.onDrop(files);
        }
    };

    handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            this.setState((state) => {
                const oldIndex = state.media.findIndex((item) => item.id === active.id);
                const newIndex = state.media.findIndex((item) => item.id === over.id);

                return {
                    media: arrayMove(state.media, oldIndex, newIndex),
                };
            });
        }
    };

    updateLocalStorage = () => {
        let isActiveSuccess;

        const setStatus = this.state.setStatus;

        if (setStatus === "Ready for Distribution" || setStatus === "Empty") {
            isActiveSuccess = false;
        } else {
            isActiveSuccess = true;
        }

        const itemLocalStorage = {
            collectionId: this.state.collectionId,
            collectionName: this.state.collectionName,
            tier: this.state.tier,
            copyright: this.state.copyright,
            notes: this.state.notes,
            createdAt: this.state.createdAt,
            setStatus: this.state.setStatus,
            activeMonth: this.state.activeMonth,
            monthValue: this.state.monthValue,
            isActiveSuccess: isActiveSuccess,
        };

        // Use user-specific key to prevent cross-user data contamination
        const userSpecificKey = `collection_${this.state.id}_${this.props.user?.sub || 'unknown'}`;
        localStorage.setItem(userSpecificKey, JSON.stringify(itemLocalStorage));
    };

    getMediaEndpoint = async () => {
        this.initGetMedia = true;
        if (this.props.active) {
            this.intervalID = setInterval(this.getMediaEndpoint.bind(this), 10000);
        }
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            const response = await CollectionsApi.getMedia(token, this.state.id);

            if (response.success) {
                const responseJson = response.data;

                // Check if credentials are available
                if (!this.props.AWS?.config?.credentials) {
                    console.error("AWS credentials are missing for getMedia");
                    toast.error("AWS credentials not available. Please try refreshing the page.");
                    this.setState({ loading: false });
                    return;
                }

                const s3 = new S3Client({
                    region: "us-east-1",
                    credentials: this.props.AWS.config.credentials,
                });
                this.setState({ mediaCount: responseJson.length });

                // Process all media and collect their URLs
                const mediaPromises = responseJson.map(async (item, index) => {
                    let filename = item.fileName.replace(/\.[^/.]+$/, "") + ".jpg";
                    let key =
                        "collections/" +
                        this.state.collectionOwnerId +
                        "/" +
                        this.state.collectionId +
                        "/thumbs/" +
                        filename;

                    const command = new GetObjectCommand({
                        Bucket: UPLOADS_BUCKET,
                        Key: key,
                        // Prevent stale caching without breaking the signature
                        ResponseCacheControl: 'no-cache'
                    });

                    try {
                        // Add timestamp to help with cache busting
                        const urlExpiryTime = Math.floor(Date.now() / 1000) + 3600; // 1 hour from now
                        const url = await getSignedUrl(s3, command, { expiresIn: 3600 });

                        return {
                            src: url,
                            id: item.id,
                            width: 0.5,
                            height: 0.5,
                            status: item.mediaStatus || 'UNKNOWN', // Support new mediaStatus; fall back when missing
                            f: item.fileName,
                            alt: "thumbs",
                            key: `${item.id}_${this.state.collectionId}_${this.state.user.sub}`, // Unique key to prevent cross-contamination
                            uniqueKey: `${item.id}_${this.state.collectionId}_${this.state.user.sub}_${urlExpiryTime}`, // Even more unique for React keys
                            orderId: item.orderId || index, // Use orderId if available, fallback to index
                            userId: this.state.user.sub, // Track which user this image belongs to
                            setId: this.state.collectionId, // Track which collection this belongs to
                            // SS-461: surface timestamps so the stale-processing
                            // detector on the client and the backend can
                            // compute "is this stuck?" correctly.
                            createdAt: item.createdAt,
                            updatedAt: item.updatedAt,
                        };
                    } catch (err) {
                        console.warn(`Failed to get thumb for ${item.fileName}:`, err);
                        return null;
                    }
                });

                // Wait for all media to be processed
                const processedMedia = await Promise.all(mediaPromises);

                // Filter out failed media and sort by orderId
                const validMedia = processedMedia
                    .filter(media => media !== null)
                    .sort((a, b) => (a.orderId || 0) - (b.orderId || 0));

                // Only update state if component is still mounted
                if (this._isMounted) {
                    this.setState({
                        media: validMedia,
                        mediaLoaded: true, // Mark media as successfully loaded
                    });
                }
            } else {
                toast.warn(
                    `Something went wrong: ${response.message || 'Failed to load media'}`,
                    10000
                );
            }
            this.setState({ loading: false });
        } catch (err) {
            console.error("Error getting collection media from endpoint:", err);
            toast.error("Failed to load media: " + (err.message || err));
            this.setState({ loading: false });
        }
    };

    componentWillUnmount() {
        this._isMounted = false;
        window.onbeforeunload = null;
        document.removeEventListener("keydown", this.escFunction, false);

        clearInterval(this.intervalID);

        // Clear processing monitor if it exists
        if (this.processingMonitorId) {
            clearInterval(this.processingMonitorId);
        }

        // SS-470: stop the delta-poll timer
        this.stopDeltaPolling();
        if (this.statusCheckTimeoutId) {
            clearTimeout(this.statusCheckTimeoutId);
            this.statusCheckTimeoutId = null;
        }

        // Clear status check timeout if it exists
        if (this.statusCheckTimeoutId) {
            clearTimeout(this.statusCheckTimeoutId);
        }
    }

    startEditNote = (ev) => {
        const notes = this.state.notes;
        if (this._isMounted) {
            this.setState({ editNote: true, notes: notes });
        }
    };

    startEditCopyright = (ev) => {
        const copyright = this.state.copyright;
        if (this._isMounted) {
            this.setState({ editCopyright: true, copyright: copyright });
        }
    };

    startEditName = (ev) => {
        const name = this.state.collectionName;
        if (this._isMounted) {
            this.setState({ editName: true, collectionName: name });
        }
    };

    handleEditNote = (ev) => {
        if (this._isMounted) {
            this.setState({ notes: ev.target.value });
        }
    };

    handleNotesKeyPress = (ev) => {
        if (ev.key === 'Enter' && !ev.shiftKey) {
            ev.preventDefault();
            this.editNoteSubmit(ev);
        }
    };

    handleCopyright = (ev) => {
        if (this._isMounted) {
            this.setState({ copyright: ev.target.value });
        }
    };

    handleEditName = (ev) => {
        if (this._isMounted) {
            this.setState({ collectionName: ev.target.value });
        }
    };

    keyPress(e) {
        if (e.key === "Escape") {
            this.setState({
                editNote: false,
                editCopyright: false,
                editName: false,
                notes: this.state.oldNotes,
                copyright: this.state.oldCopyright,
                collectionName: this.state.oldName,
            });
        }
    }

    addErrorSrc = (e) => {
        e.target.src = SomethingWrong;
        console.log("error works!");
    };

    editNoteSubmit = async (ev) => {
        ev.preventDefault();

        if (this.state.notes === "") {
            return;
        }

        this.setState({ disableNotes: true });

        try {
            await this.updateCollection("notes", this.state.notes);
            if (this._isMounted) {
                this.setState({
                    editNote: false,
                    oldNotes: this.state.notes,
                    disableNotes: false,
                });
                this.updateLocalStorage();
            }
        } catch (error) {
            this.setState({ disableNotes: false });
            toast.error("Error updating notes: " + error);
        }
    };

    editCopyrightSubmit = async (ev) => {
        ev.preventDefault();

        if (this.state.copyright === "") {
            return;
        }

        this.setState({ disableCopyright: true });

        try {
            await this.updateCollection("copyright", this.state.copyright);
            if (this._isMounted) {
                this.setState({
                    editCopyright: false,
                    oldCopyright: this.state.copyright,
                    disableCopyright: false,
                });
                this.updateLocalStorage();
            }
        } catch (error) {
            this.setState({ disableCopyright: false });
            toast.error("Error updating copyright: " + error);
        }
    };

    editNameSubmit = async (ev) => {
        ev.preventDefault();

        if (this.state.collectionName === "") {
            toast.warn("Name cannot be empty");
            return;
        }

        this.setState({ disableName: true });

        try {
            await this.updateCollection("name", this.state.collectionName);
            if (this._isMounted) {
                this.setState({
                    editName: false,
                    oldName: this.state.collectionName,
                    disableName: false,
                });
                this.updateLocalStorage();
            }
        } catch (error) {
            this.setState({ disableName: false });
            toast.error("Error updating name: " + error);
        }
    };

    handleTierChange = async (color, id, name, tierId) => {
        try {
            await this.updateCollection("tier", tierId);
            if (this._isMounted) {
                this.setState({ tier: tierId });
                this.updateLocalStorage();
            }
        } catch (error) {
            toast.error("Error updating tier: " + error);
        }
    };

    toggleSortMode = async () => {
        if (this.state.sortMode) {
            // If we're currently in sort mode and user clicks "Save Sort", save the order
            await this.sendReorderedList();
            // Then exit sort mode
            this.setState({ sortMode: false, sortDesc: "Sort Media" });
        } else {
            // Enter sort mode
            this.setState({ sortMode: true, sortDesc: "Save Sort" });
        }
    };

    // Helper method to create a proper Date object from Unix timestamp
    // This avoids timezone issues by creating a local date for the same month/year
    createDateFromUnix = (unixTimestamp) => {
        if (!unixTimestamp) {
            return new Date();
        }

        // Get the UTC date from the timestamp
        const utcMoment = Moment.unix(unixTimestamp).utc();
        const year = utcMoment.year();
        const month = utcMoment.month(); // 0-based

        // Create a local date for the 15th of that month to avoid timezone edge cases
        const resultDate = new Date(year, month, 15);

        return resultDate;
    };

    handleChangeActiveMonth = async () => {
        try {
            // Create timestamp for the 15th day of the selected month/year to avoid timezone issues
            const selectedDate = this.state.monthValue;
            if (!selectedDate) {
                toast.error("Please select a valid month");
                return;
            }

            console.log("handleChangeActiveMonth - selectedDate:", selectedDate, "type:", typeof selectedDate);

            let year, month;

            // Handle different date types that might be in state
            if (selectedDate instanceof Date) {
                year = selectedDate.getFullYear();
                month = selectedDate.getMonth(); // 0-based
            } else if (typeof selectedDate === 'number') {
                // If it's a Unix timestamp, convert it first
                const dateFromUnix = new Date(selectedDate * 1000);
                year = dateFromUnix.getFullYear();
                month = dateFromUnix.getMonth();
            } else {
                toast.error("Invalid date format");
                return;
            }

            // Create UTC date string and convert to Unix timestamp
            const dateString = `${year}-${String(month + 1).padStart(2, '0')}-15`;
            const activeMonth = Moment.utc(dateString, 'YYYY-MM-DD').unix();

            console.log(`Setting active month: ${dateString} -> Unix: ${activeMonth}`);

            await this.updateCollection("activeMonth", activeMonth);
            if (this._isMounted) {
                // Update both activeMonth and ensure monthValue stays in sync
                const syncedMonthValue = this.createDateFromUnix(activeMonth);
                console.log(`Success: Syncing state - activeMonth: ${activeMonth}, monthValue: ${syncedMonthValue}`);
                this.setState({
                    activeMonth: activeMonth,
                    monthValue: syncedMonthValue
                });
                this.updateLocalStorage();

                // Clear collection list cache to force refresh when user goes back
                localStorage.removeItem("collections");
                localStorage.removeItem("groupedSets");
                localStorage.removeItem("groups");
                localStorage.removeItem("collectionTiers");
                // Also clear user-prefixed cache keys written by collectionsV2
                const userSub = this.props.user?.sub;
                if (userSub) {
                    localStorage.removeItem(`collections_${userSub}`);
                    localStorage.removeItem(`groupedSets_${userSub}`);
                    localStorage.removeItem(`groups_${userSub}`);
                    localStorage.removeItem(`collectionTiers_${userSub}`);
                }
            }
        } catch (err) {
            toast.error("Error updating active month: " + err);
        }
    };

    // Version that accepts the selectedDate directly to avoid state timing issues
    handleChangeActiveMonthWithValue = async (selectedDate) => {
        try {
            if (!selectedDate) {
                toast.error("Please select a valid month");
                return;
            }

            let year, month;

            // Handle different date types
            if (selectedDate instanceof Date) {
                year = selectedDate.getFullYear();
                month = selectedDate.getMonth(); // 0-based
            } else if (typeof selectedDate === 'number') {
                // If it's a Unix timestamp, convert it first
                const dateFromUnix = new Date(selectedDate * 1000);
                year = dateFromUnix.getFullYear();
                month = dateFromUnix.getMonth();
            } else {
                toast.error("Invalid date format");
                return;
            }

            // Create UTC date string and convert to Unix timestamp
            const dateString = `${year}-${String(month + 1).padStart(2, '0')}-15`;
            const activeMonth = Moment.utc(dateString, 'YYYY-MM-DD').unix();

            await this.updateCollection("activeMonth", activeMonth);
            if (this._isMounted) {
                this.setState({
                    activeMonth: activeMonth,
                    monthValue: selectedDate
                });
                this.updateLocalStorage();

                // Clear collection list cache to force refresh when user goes back
                localStorage.removeItem("collections");
                localStorage.removeItem("groupedSets");
                localStorage.removeItem("groups");
                localStorage.removeItem("collectionTiers");
                // Also clear user-prefixed cache keys written by collectionsV2
                const userSub2 = this.props.user?.sub;
                if (userSub2) {
                    localStorage.removeItem(`collections_${userSub2}`);
                    localStorage.removeItem(`groupedSets_${userSub2}`);
                    localStorage.removeItem(`groups_${userSub2}`);
                    localStorage.removeItem(`collectionTiers_${userSub2}`);
                }
            }
        } catch (err) {
            toast.error("Error updating active month: " + err);
        }
    };

    addMediaEndpoint = async (name, size, contentType) => {
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            const { collectionId, copyright } = this.state;
            // Use service function with correct signature
            const response = await CollectionsApi.addMedia(token, {
                setId: collectionId,
                fileName: name,
                size: size,
                contentType: contentType,
            });

            if (response.success) {
                // SS-470: Return response data so caller can check for dedupe
                // (alreadyExists) or unique fileName.
                return response.data;
            } else {
                toast.warn("Something went wrong: Error " + response.status);
                return null;
            }
        } catch (err) {
            toast.error("Error adding media to endpoint: " + err);
            return null;
        }
    };

    getCollectionDetails = async () => {
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            const { id } = this.state;
            // Use service function
            const response = await CollectionsApi.getCollectionDetails(token, id);

            if (response.success) {
                const responseJson = response.data;
                if (!responseJson || responseJson.length === 0) {
                    this.setState({ loading: false, setStatus: "NOT_FOUND" });
                    return;
                }

                if (this._isMounted) {
                    const ownerId = responseJson[0].ownerUserId || (this.state.user && this.state.user.sub);
                    this.setState({
                        collectionName: responseJson[0].collectionName,
                        oldName: responseJson[0].collectionName,
                        collectionId: responseJson[0].id,
                        collectionOwnerId: ownerId,
                        copyright: responseJson[0].copyright,
                        oldCopyright: responseJson[0].copyright,
                        createdAt: responseJson[0].createdAt,
                        notes: responseJson[0].notes,
                        oldNotes: responseJson[0].notes,
                        setStatus: responseJson[0].collectionStatus || "Empty", // Add fallback
                        tier: responseJson[0].tier,
                        activeMonth: responseJson[0].activeMonth,
                        monthValue: this.createDateFromUnix(responseJson[0].activeMonth),
                        folderPath: responseJson[0].folderPath || [],
                    }, () => {
                        // Call getMedia after state is updated to ensure collectionId is available
                        this.getMedia();
                    });

                    const status = responseJson[0].collectionStatus || "Empty";
                    if (status === "SUCCESS") {
                        this.setState({ isStatusSuccess: true, allowDownload: true });
                    } else {
                        this.setState({ isStatusSuccess: false, allowDownload: false });
                    }
                }
            } else {
                toast.warn(`Something went wrong: ${response.message || 'Failed to load collection details'}`);
                this.setState({ loading: false });
            }
        } catch (err) {
            toast.error("Error getting collection list from endpoint: " + err);
        }
    };

    getMedia = async () => {
        console.log("getMedia called, initGetMedia flag:", this.initGetMedia);
        if (this.initGetMedia) {
            console.log("getMedia blocked by initGetMedia flag");
            return;
        }
        this.initGetMedia = true;
        console.log("getMedia proceeding with media loading...");
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            const { collectionId } = this.state;
            // Use service function
            const response = await CollectionsApi.getMedia(token, collectionId);

            if (response.success) {
                console.log("getMedia API response successful, processing media...", response.data.length, "media");
                console.log("Raw media data from backend:", response.data);
                const responseJson = response.data;

                // Check if credentials are available
                if (!this.props.AWS?.config?.credentials) {
                    console.error("AWS credentials are missing for getMedia");
                    toast.error("AWS credentials not available. Please try refreshing the page.");
                    this.setState({ loading: false });
                    return;
                }

                const s3 = new S3Client({
                    region: "us-east-1",
                    credentials: this.props.AWS.config.credentials,
                });
                this.setState({ mediaCount: responseJson.length });

                // Process all media and collect their URLs
                const mediaPromises = responseJson.map(async (item, index) => {
                    let filename = item.fileName.replace(/\.[^/.]+$/, "") + ".jpg";
                    let key =
                        "collections/" +
                        this.state.collectionOwnerId +
                        "/" +
                        this.state.collectionId +
                        "/thumbs/" +
                        filename;

                    const command = new GetObjectCommand({
                        Bucket: UPLOADS_BUCKET,
                        Key: key,
                    });

                    try {
                        const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
                        return {
                            src: url,
                            id: item.id,
                            width: 0.5,
                            height: 0.5,
                            status: item.mediaStatus || 'UNKNOWN', // Use mediaStatus from backend
                            f: item.fileName,
                            fileName: item.fileName,
                            contentType: item.contentType,
                            createdAt: item.createdAt,
                            updatedAt: item.updatedAt,
                            alt: "thumbs",
                            orderId: item.orderId || index, // Use orderId if available, fallback to index
                        };
                    } catch (err) {
                        console.warn(`Failed to get thumb for ${item.fileName}:`, err);
                        return null;
                    }
                });

                // Wait for all media to be processed
                const processedMedia = await Promise.all(mediaPromises);

                // Filter out failed media and sort by orderId
                const validMedia = processedMedia
                    .filter(media => media !== null)
                    .sort((a, b) => (a.orderId || 0) - (b.orderId || 0));

                console.log("Processed media:", validMedia.length, "valid media out of", processedMedia.length, "total");

                if (this._isMounted) {
                    // On first load, set the base media count only if we haven't started any uploads
                    let currentTotalExpected = this.state.totalExpectedCount;
                    if (!this.state.mediaLoaded && this.state.totalExpectedCount === 0) {
                        // Only set initial counts if we haven't tracked any uploads yet
                        console.log(`Initial media load: ${validMedia.length} media found, no uploads tracked yet`);
                        currentTotalExpected = validMedia.length;
                        this.setState({
                            baseMediaCount: validMedia.length,
                            totalExpectedCount: validMedia.length,
                            mediaLoaded: true
                        });
                    } else if (!this.state.mediaLoaded) {
                        // First load but we already have an expected count (from uploads), preserve it
                        console.log(`Initial media load: ${validMedia.length} media found, preserving expected count of ${this.state.totalExpectedCount}`);
                        this.setState({
                            baseMediaCount: validMedia.length,
                            mediaLoaded: true
                        });
                    } else {
                        // On subsequent loads, use totalExpectedCount for comparison
                        console.log(`Media refresh: Found ${validMedia.length} media, expected ${this.state.totalExpectedCount}`);
                    }

                    // SS-470: Start delta polling after first media load.
                    // startDeltaPolling has a guard (if _deltaPollTimer exists)
                    // so calling on every getMedia() is safe — only starts once.
                    this.startDeltaPolling();

                    // Calculate collection status based on media statuses with detailed counts
                    let newSetStatus = "Empty";
                    let newStatusColor = "#DDDDDD";

                    // Count media by status
                    let successCount = 0;
                    let processingCount = 0;
                    let failedCount = 0;
                    let emptyCount = 0;
                    let errorMedia = []; // Track which media have errors

                    validMedia.forEach(media => {
                        switch (media.status) {
                            case "SUCCESS":
                                successCount++;
                                break;
                            case "PROCESSING":
                            case "UPLOADED":
                            case "Processing":
                            case "Uploaded":
                                processingCount++;
                                break;
                            case "FAILED":
                            case "Failed":
                                failedCount++;
                                errorMedia.push(media);
                                break;
                            default:
                                emptyCount++;
                                if (media.f || media.fileName) {
                                    errorMedia.push(media);
                                }
                        }
                    });

                    console.log(`Media status counts: Total=${validMedia.length}, Success=${successCount}, Processing=${processingCount}, Failed=${failedCount}, Empty=${emptyCount}`);
                    console.log(`Expected total: ${currentTotalExpected}, Current total: ${validMedia.length}`);

                    // Determine status based on your detailed requirements
                    if (validMedia.length === 0) {
                        newSetStatus = "Empty";
                        newStatusColor = "#DDDDDD";
                    } else if (processingCount > 0) {
                        // If any media are still processing, show Processing status
                        newSetStatus = "Processing";
                        newStatusColor = "#ffc107"; // Yellow for processing
                    } else if (validMedia.length < currentTotalExpected) {
                        // If we have fewer media than expected, still processing (uploads may be in progress)
                        newSetStatus = "Processing";
                        newStatusColor = "#ffc107"; // Yellow for processing
                    } else if (successCount === currentTotalExpected && validMedia.length === currentTotalExpected) {
                        // All expected media are present and successful
                        newSetStatus = "Ready for Distribution";
                        newStatusColor = "#28a745"; // Green for success
                    } else if (failedCount > 0 || emptyCount > 0) {
                        // Show error status with details for failed or empty status media
                        newSetStatus = "Error with Processing";
                        newStatusColor = "#dc3545"; // Red for errors

                        // Show toast with problematic media
                        if (errorMedia.length > 0) {
                            // SS-470: dedup per mediaId so the toast doesn't re-fire
                            // every 5s while we wait for processing.
                            const newFailedIds = errorMedia
                                .map((m) => m.id)
                                .filter((id) => id && !this.state._shownErrorToastIds.has(id));
                            if (newFailedIds.length > 0) {
                                const maxMediaToShow = 5;
                                const showNames = errorMedia
                                    .filter((m) => newFailedIds.includes(m.id))
                                    .map((m) => m.f || m.fileName)
                                    .slice(0, maxMediaToShow);
                                const remainingCount = newFailedIds.length - showNames.length;
                                let errorMessage = `Media not in SUCCESS status: ${showNames.join(', ')}`;
                                if (remainingCount > 0) errorMessage += ` and ${remainingCount} more`;
                                toast.error(errorMessage, { autoClose: 8000 });
                                const updated = new Set(this.state._shownErrorToastIds);
                                newFailedIds.forEach((id) => updated.add(id));
                                this.setState({ _shownErrorToastIds: updated });
                            }
                        }
                    } else {
                        // Default fallback - shouldn't reach here with the above logic
                        newSetStatus = "Waiting for Processing";
                        newStatusColor = "#6c757d"; // Gray for waiting
                    }

                    console.log(`Collection status set to: ${newSetStatus} with color: ${newStatusColor}`);

                    // Set allowDownload based on status
                    const allowDownload = newSetStatus === "Ready for Distribution";
                    console.log(`Setting allowDownload to: ${allowDownload} for status: ${newSetStatus}`);

                    this.setState({
                        media: validMedia,
                        setStatus: newSetStatus,
                        statusColor: newStatusColor,
                        expectedMediaCount: currentTotalExpected, // Use the correct expected count
                        successMediaCount: successCount,
                        processingMediaCount: processingCount,
                        failedMediaCount: failedCount,
                        emptyMediaCount: emptyCount,
                        allowDownload: allowDownload,
                        isStatusSuccess: allowDownload, // This also controls some UI elements
                    });
                }
            } else {
                toast.warn(
                    `Something went wrong: ${response.message || 'Failed to load media'}`,
                    10000
                );
                // Even on error, mark media as "loaded" to show the error state
                this.setState({ mediaLoaded: true });
            }
            this.setState({ loading: false });
        } catch (err) {
            console.error("Error getting collection media from endpoint:", err);
            toast.error("Failed to load media: " + (err.message || err));
            // Mark as loaded even on error
            this.setState({ loading: false, mediaLoaded: true });
        } finally {
            // SS-470: Reset the re-entrancy guard so future calls (from
            // _pollMediaStatus when hasNewItems, or from checkMediaStatusUntilComplete)
            // are not blocked. Without this, the delta poll timer finds new items
            // but getMedia() silently returns because initGetMedia is still true
            // from the initial page load.
            this.initGetMedia = false;
        }
    };

    // Helper method to refresh collection status after media operations
    refreshCollectionStatus = () => {
        console.log("Refreshing collection status...");
        this.initGetMedia = false; // Reset flag to allow re-fetching
        this.getMedia();
    };

    // Method to handle post-upload status monitoring
    startPostUploadMonitoring = async (uploadedMediaCount) => {
        console.log(`Starting post-upload monitoring for ${uploadedMediaCount} new media`);

        // Clear any existing monitoring to avoid conflicts
        if (this.statusCheckTimeoutId) {
            clearTimeout(this.statusCheckTimeoutId);
            this.statusCheckTimeoutId = null;
        }

        // Use the already updated totalExpectedCount from the upload method
        const newTotalExpected = this.state.totalExpectedCount;

        console.log(`Upload tracking: New uploads=${uploadedMediaCount}, Total expected=${newTotalExpected}, Current media=${this.state.media.length}`);

        // Set status to Processing using the API call
        await this.updateCollectionStatus("Processing");

        // Update the local state for UI display
        this.setState({
            statusColor: "#ffc107",
            expectedMediaCount: newTotalExpected, // Ensure display count is updated
            statusCheckAttempts: 0,
            lastUploadCount: uploadedMediaCount
        });

        // SS-493: Reset wall-clock timeout tracking
        this._lastProcessingCount = null;
        this._lastProcessingCountTimestamp = null;

        // Start checking status with delay to allow processing
        this.statusCheckTimeoutId = setTimeout(() => {
            this.checkMediaStatusUntilComplete();
        }, 3000); // Give 3 seconds for initial processing
    };

    markStaleProcessingMediaAsFailed = async () => {
        const now = Math.floor(Date.now() / 1000);
        const staleMedia = (this.state.media || []).filter((media) => {
            const status = String(media.status || '').toUpperCase();
            if (status !== 'PROCESSING' && status !== 'UPLOADED') {
                return false;
            }

            const fileName = String(media.fileName || media.f || '').toLowerCase();
            const isVideo = ['.mp4', '.mov', '.avi', '.webm', '.mkv'].some((ext) => fileName.endsWith(ext));
            const thresholdSeconds = isVideo ? 600 : 60;

            const updatedAt = Number(media.updatedAt || 0);
            const createdAt = Number(media.createdAt || 0);
            const refTs = Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : createdAt;
            if (!refTs || !Number.isFinite(refTs)) {
                return false;
            }

            return now - refTs > thresholdSeconds;
        });

        if (!staleMedia.length) {
            return 0;
        }

        let token = this.props.token;
        if (typeof token === 'function') {
            token = await token();
        }

        const updates = staleMedia.map(async (media) => {
            try {
                await CollectionsApi.updateMediaStatus(token, media.id, 'FAILED');
                return true;
            } catch (error) {
                console.error('Failed to mark media as FAILED', media?.id, error);
                return false;
            }
        });

        const results = await Promise.all(updates);
        const updatedCount = results.filter(Boolean).length;

        if (updatedCount > 0) {
            toast.error(`${updatedCount} media item(s) timed out during processing and were marked as failed. Remove and re-upload those files.`);
        }

        return updatedCount;
    };

    // Method to continuously check media status until all are processed
    /**
     * SS-470: Lightweight delta poll. Calls GET /v1/media/status and patches
     * the existing state.media[] in place. Only items whose `updatedAt`
     * changed are updated; signed URLs are preserved for everything else.
     *
     * Also performs the silent 1-attempt auto-redrive for FAILED items that
     * haven't been auto-redriven yet (and that aren't `temp_source_expired`).
     */
    _pollMediaStatus = async () => {
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            if (!token) return;

            const res = await CollectionsApi.getMediaStatus(
                token,
                this.state.collectionId,
                this.state._lastStatusPollAt || 0,
            );

            if (!res?.success || !Array.isArray(res.data?.items)) return;

            const items = res.data.items;
            const since = res.data.since || Math.floor(Date.now() / 1000);

            // Build a lookup of current media by id
            const byId = new Map(this.state.media.map((m) => [m.id, m]));
            const updatedMedia = [...this.state.media];
            let mediaChanged = false;
            let hasNewItems = false;
            const autoRedriveCandidates = [];

            // Prepare S3 client for thumbnail signing (only if needed)
            let s3Client = null;
            const getS3Client = () => {
                if (!s3Client && this.props.AWS?.config?.credentials) {
                    s3Client = new S3Client({
                        region: "us-east-1",
                        credentials: this.props.AWS.config.credentials,
                    });
                }
                return s3Client;
            };

            for (const item of items) {
                const existingIdx = updatedMedia.findIndex((m) => m.id === item.id);
                if (existingIdx === -1) {
                    // New item not in current list — need full getMedia
                    hasNewItems = true;
                    continue;
                }
                const existing = updatedMedia[existingIdx];
                const newUpdatedAt = Number(item.updatedAt || 0);
                const existingUpdatedAt = Number(existing.updatedAt || 0);
                if (newUpdatedAt !== existingUpdatedAt) {
                    mediaChanged = true;
                    const prevStatus = existing.status;
                    const newStatus = item.mediaStatus || existing.status;

                    // Update in-place — only change status + updatedAt.
                    // Do NOT touch src (avoids re-fetching all thumbnails).
                    updatedMedia[existingIdx] = {
                        ...existing,
                        status: newStatus,
                        updatedAt: item.updatedAt,
                    };

                    // If transitioned to SUCCESS, re-sign the thumbnail URL
                    // to force a re-fetch (the S3 object now exists).
                    if (prevStatus !== 'SUCCESS' && newStatus === 'SUCCESS') {
                        const s3 = getS3Client();
                        if (s3) {
                            try {
                                const thumbName = item.fileName.replace(/\.[^/.]+$/, "") + ".jpg";
                                const key = `collections/${this.state.collectionOwnerId}/${this.state.collectionId}/thumbs/${thumbName}`;
                                const command = new GetObjectCommand({ Bucket: UPLOADS_BUCKET, Key: key });
                                const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
                                updatedMedia[existingIdx].src = url;
                            } catch (err) {
                                console.warn(`Failed to sign thumb for ${item.fileName}:`, err);
                            }
                        }
                    }
                }
                if (item.mediaStatus === 'FAILED'
                    && item.processingError !== 'temp_source_expired'
                    && !this.state._autoRedrivenIds.has(item.id)) {
                    autoRedriveCandidates.push(item.id);
                }
            }

            const nextState = { _lastStatusPollAt: since };
            if (mediaChanged) {
                nextState.media = updatedMedia;
            }
            if (hasNewItems) {
                // New items appeared — need full getMedia to get them properly
                await this.getMedia();
            }
            if (autoRedriveCandidates.length > 0) {
                const updatedAuto = new Set(this.state._autoRedrivenIds);
                for (const id of autoRedriveCandidates) {
                    updatedAuto.add(id);
                    // Silent auto-redrive. Failures surface via the next poll
                    // or the manual button. We deliberately don't toast on
                    // failure — the user opted into manual control after 1 attempt.
                    CollectionsApi.redriveMedia(token, id).catch((err) => {
                        console.warn('auto-redrive failed', id, err?.message || err);
                    });
                }
                nextState._autoRedrivenIds = updatedAuto;
            }

            // SS-493: Recompute status counts from the in-place-updated media
            // array on every delta-poll tick. Without this, processingMediaCount
            // is frozen at its seed value (only updated by full getMedia()),
            // and the terminal-state stop condition never triggers.
            const countMedia = mediaChanged ? updatedMedia : this.state.media;
            let sc = 0, pc = 0, fc = 0, ec = 0;
            countMedia.forEach(m => {
                const s = String(m.status || '').toUpperCase();
                if (s === 'SUCCESS') sc++;
                else if (s === 'PROCESSING' || s === 'UPLOADED') pc++;
                else if (s === 'FAILED') fc++;
                else ec++;
            });
            nextState.successMediaCount = sc;
            nextState.processingMediaCount = pc;
            nextState.failedMediaCount = fc;
            nextState.emptyMediaCount = ec;

            this.setState(nextState);
        } catch (err) {
            // Don't toast on poll failure; the full getMedia will surface issues.
            console.warn('_pollMediaStatus failed', err?.message || err);
        }
    };

    /**
     * SS-470: starts a 5s delta-poll timer. Stops automatically on unmount
     * via componentWillUnmount.
     */
    startDeltaPolling = () => {
        if (this._deltaPollTimer) return;
        const tick = async () => {
            await this._pollMediaStatus();
            // SS-493: Stop background delta poll when all items are terminal
            // (SUCCESS/FAILED). Prevents indefinite polling after uploads complete.
            const { media, processingMediaCount, failedMediaCount, emptyMediaCount } = this.state;
            const allTerminal = processingMediaCount === 0 && emptyMediaCount === 0;
            if (this._isMounted) {
                if (allTerminal) {
                    this._deltaPollTimer = null;
                    return;
                }
                this._deltaPollTimer = setTimeout(tick, 5000);
            }
        };
        this._deltaPollTimer = setTimeout(tick, 5000);
    };

    stopDeltaPolling = () => {
        if (this._deltaPollTimer) {
            clearTimeout(this._deltaPollTimer);
            this._deltaPollTimer = null;
        }
    };

    checkMediaStatusUntilComplete = async () => {
        try {
            console.log(`Status check attempt ${this.state.statusCheckAttempts + 1}`);

            // SS-470: Use delta poll instead of full getMedia() to avoid
            // re-signing ALL thumbnails on every 5s tick. _pollMediaStatus
            // updates status in-place and only signs thumbnails for items
            // that transitioned to SUCCESS.
            this.initGetMedia = false;
            await this._pollMediaStatus();

            const staleFailedCount = await this.markStaleProcessingMediaAsFailed();
            if (staleFailedCount > 0) {
                // Stale items were marked FAILED — _pollMediaStatus will pick
                // up the status change on the next tick. No need for full getMedia().
                await this._pollMediaStatus();
            }

            const attempts = this.state.statusCheckAttempts + 1;
            this.setState({ statusCheckAttempts: attempts });

            // Check if we need to continue monitoring
            const {
                totalExpectedCount,
                successMediaCount,
                processingMediaCount,
                maxStatusCheckAttempts
            } = this.state;

            // SS-493: Wall-clock timeout — stop polling if processingMediaCount
            // hasn't dropped in 60s (stuck uploads / permanent PROCESSING items).
            const now = Date.now();
            if (!this._lastProcessingCountTimestamp || processingMediaCount !== this._lastProcessingCount) {
                this._lastProcessingCount = processingMediaCount;
                this._lastProcessingCountTimestamp = now;
            }
            const stuckMs = now - this._lastProcessingCountTimestamp;

            console.log(`Current status: Expected=${totalExpectedCount}, Success=${successMediaCount}, Processing=${processingMediaCount}, Current media=${this.state.media.length}, StuckMs=${stuckMs}`);

            // Continue checking if:
            // 1. We still have media processing
            // 2. Processing count is not stuck (hasn't changed in 60s)
            // 3. Haven't reached max attempts
            const needsContinueMonitoring = (
                processingMediaCount > 0 &&
                stuckMs < 60000 &&
                attempts < maxStatusCheckAttempts
            );

            if (needsContinueMonitoring) {
                console.log(`Continuing status monitoring (attempt ${attempts}/${maxStatusCheckAttempts})`);
                this.statusCheckTimeoutId = setTimeout(() => {
                    this.checkMediaStatusUntilComplete();
                }, 5000); // Check every 5 seconds
            } else {
                console.log("Status monitoring complete");
                this.statusCheckTimeoutId = null; // Clear timeout ID when monitoring completes

                // Check if all media are successfully processed
                if (successMediaCount >= totalExpectedCount && processingMediaCount === 0) {
                    console.log("All media processed successfully. Setting status to Ready for Distribution.");
                    await this.updateCollectionStatus("Ready for Distribution");
                    toast.success("All media processed successfully! Set is now ready for distribution.");
                } else if (attempts >= maxStatusCheckAttempts) {
                    console.warn("Reached maximum status check attempts");
                    toast.warning("Media processing is taking longer than expected. Status may not be accurate.");
                } else if (processingMediaCount > 0 && stuckMs >= 60000) {
                    // SS-493: Timeout — no status changes for 60s
                    console.warn("Status monitoring timed out — no changes for 60s");
                    toast.warning("Media processing appears stuck. Some items may need to be re-uploaded.");
                }
            }

        } catch (error) {
            console.error("Error during status check:", error);
            // Don't stop monitoring on error, just delay a bit more
            if (this.state.statusCheckAttempts < this.state.maxStatusCheckAttempts) {
                this.statusCheckTimeoutId = setTimeout(() => {
                    this.checkMediaStatusUntilComplete();
                }, 10000); // Wait longer on error
            } else {
                this.statusCheckTimeoutId = null; // Clear timeout ID when giving up
            }
        }
    };

    updateCollection = async (field, value) => {
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            const { collectionId } = this.state;
            // Map UI field names to API update types
            const fieldMapping = {
                notes: 'notes',
                copyright: 'copyright',
                name: 'collectionName',
                photosetName: 'collectionName',
                collectionName: 'collectionName',
                tier: 'tier',
                status: 'collectionStatus',
                collectionStatus: 'collectionStatus',
                activeMonth: 'activeMonth',
                previewImage: 'previewImage',
            };

            const updateType = fieldMapping[field] || field;

            // Use CollectionsApi to patch collection
            const response = await CollectionsApi.updateCollection(token, {
                type: updateType,
                collectionId,
                data: value,
            });

            if (response.success) {
                toast.success("Collection updated");
            } else {
                toast.warn(`Something went wrong: ${response.message || 'Update failed'}`);
            }
        } catch (err) {
            console.error("Error updating collection:", err);
            toast.error("Error updating collection: " + err.message);
        }
    };

    /**
     * Update collection status specifically using the direct API call
     * This method is used for automatic status transitions and doesn't show success toasts
     */
    updateCollectionStatus = async (status) => {
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            const { collectionId } = this.state;

            console.log(`Updating collection status to: ${status}`);

            // Use the direct CollectionsApi method for status updates
            const response = await CollectionsApi.updateCollectionStatus(token, collectionId, status);

            if (response.success) {
                // Update local state to reflect the new status
                this.setState({ setStatus: status });
                console.log(`Collection status successfully updated to: ${status}`);

                // Clear collection list cache to force refresh when user goes back - use user-specific keys
                const userId = this.props.user?.sub || 'unknown';
                localStorage.removeItem(`collections_${userId}`);
                localStorage.removeItem(`groupedSets_${userId}`);
                localStorage.removeItem(`groups_${userId}`);
                localStorage.removeItem(`collectionTiers_${userId}`);
            } else {
                console.error(`Failed to update collection status: ${response.message || 'Status update failed'}`);
                toast.warn(`Failed to update status: ${response.message || 'Status update failed'}`);
            }
        } catch (err) {
            console.error("Error updating collection status:", err);
            toast.error("Error updating collection status: " + err.message);
        }
    };

    // Optimistically decrement media-related counts after a soft delete
    decrementCountsAfterDelete = (deletedId) => {
        this.setState((prev) => {
            const updatedMedia = prev.media.filter((m) => m.id !== deletedId);
            const removedItem = prev.media.find((m) => m.id === deletedId);

            const newExpected = Math.max(0, (prev.expectedMediaCount || 0) - 1);
            const newTotalExpected = Math.max(0, (prev.totalExpectedCount || 0) - 1);
            const newMediaCount = Math.max(0, (prev.mediaCount || 0) - 1);
            const newSuccess = removedItem && removedItem.status === 'SUCCESS'
                ? Math.max(0, (prev.successMediaCount || 0) - 1)
                : prev.successMediaCount;

            return {
                media: updatedMedia,
                expectedMediaCount: newExpected,
                totalExpectedCount: newTotalExpected,
                mediaCount: newMediaCount,
                successMediaCount: newSuccess,
            };
        });
    };

    deleteCollection = async () => {
        confirm(
            "Are you sure you want to delete this collection? This cannot be undone.",
            "Delete Collection"
        ).then(
            async () => {
                try {
                    let token = this.props.token;
                    if (typeof token === 'function') {
                        token = await token();
                    }
                    const { collectionId } = this.state;
                    // Use service function
                    const response = await CollectionsApi.deleteCollection(token, collectionId);

                    if (response.success) {
                        toast.success("Collection deleted");

                        // Clear collection list cache to force refresh when user goes back
                        const userId = this.props.user?.sub || 'unknown';
                        localStorage.removeItem(`collections_${userId}`);
                        localStorage.removeItem(`groupedSets_${userId}`);
                        localStorage.removeItem(`groups_${userId}`);
                        localStorage.removeItem(`collectionTiers_${userId}`);

                        // Also remove this specific collection's cache
                        const userSpecificKey = `collection_${this.state.id}_${userId}`;
                        localStorage.removeItem(userSpecificKey);

                        // Navigate back to collections list and force page reload to clear any cached thumbnails
                        this.props.navigate("/collections");
                        setTimeout(() => window.location.reload(), 100);
                    } else {
                        toast.warn(`Something went wrong: ${response.message || 'Delete failed'}`);
                    }
                } catch (err) {
                    console.error("Error deleting collection:", err);
                    toast.error("Error deleting collection: " + (err.message || err));
                }
            },
            () => { }
        );
    };

    deleteMedia = async () => {
        confirm(
            "Are you sure you want to delete this media? This cannot be undone.",
            "Delete Media"
        ).then(
            async () => {
                try {
                    let token = this.props.token;
                    if (typeof token === 'function') {
                        token = await token();
                    }
                    const { id, currentImageId } = this.state;
                    // Use service function
                    const response = await CollectionsApi.deleteMedia(token, { setId: id, mediaId: currentImageId });

                    if (response.success) {
                        toast.success("Media deleted");
                        // Decrement expected counts locally and refresh
                        this.decrementCountsAfterDelete(currentImageId);
                        this.setState({ showModal: false });
                        this.refreshCollectionStatus(); // Use helper method to refresh status
                    } else {
                        toast.warn(`Something went wrong: ${response.message || 'Delete failed'}`);
                    }
                } catch (err) {
                    console.error("Error deleting media:", err);
                    toast.error("Error deleting media: " + (err.message || err));
                }
            },
            () => { }
        );
    };

    downloadCollection = async () => {
        this.setState({ isRequesting: true });
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            const { collectionId } = this.state;
            // Use service function
            const response = await CollectionsApi.downloadCollection(token, { setId: collectionId });

            if (response.success) {
                toast.success(
                    "Your download is being prepared. You will receive a download notification shortly."
                );
            } else {
                toast.warn(`Something went wrong: ${response.message || 'Download failed'}`);
            }
        } catch (err) {
            console.error("Error downloading collection:", err);
            toast.error("Error downloading collection: " + (err.message || err));
        }
        this.setState({ isRequesting: false });
    };

    setDistributionStatus = async (status) => {
        if (status !== this.state.setStatus) {
            try {
                let token = this.props.token;
                if (typeof token === 'function') {
                    token = await token();
                }
                const { collectionId } = this.state;
                const response = await CollectionsApi.updateCollectionStatus(token, collectionId, status);

                if (response.success) {
                    toast.success(`Status updated to ${status}`);
                    this.setState({ setStatus: status });
                } else {
                    toast.warn(`Something went wrong: ${response.message || 'Status update failed'}`);
                }
            } catch (err) {
                console.error("Error setting distribution status:", err);
                toast.error("Error setting distribution status: " + err.message);
            }
        }
    };

    sendReorderedList = async () => {
        try {
            let token = this.props.token;
            if (typeof token === 'function') {
                token = await token();
            }
            // Build reordered list directly for CollectionsApi.updateMediaOrder
            const reorderedList = this.state.media.map((item, index) => ({
                mediaId: item.id,
                orderId: index,
            }));

            console.log("Sending reordered list:", reorderedList);

            const response = await CollectionsApi.updateMediaOrder(token, reorderedList);

            if (response.success) {
                toast.success("Sort saved", { autoClose: 10000 });
            } else {
                toast.warn(`Sort Request Problem: ${response.message || 'Update failed'}`, { autoClose: 10000 });
            }
        } catch (err) {
            console.error("Error updating media order:", err);
            toast.warn("Sort Request Error: " + (err.message || err), { autoClose: 10000 });
        }
    };

    // ... (rest of the component remains unchanged)

    render() {
        const makeText = (m) => {
            return Moment.unix(m).format("MMMM YYYY");
        };

        const pickerLang = {
            months: [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
            ],
            from: "From",
            to: "To",
        };

        if (this.state.loading) {
            return (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                >
                    <CircularProgress />
                </Box>
            );
        }

        if (this.state.setStatus === "NOT_FOUND") {
            return (
                <Box textAlign="center" p={4}>
                    <Typography variant="h4" gutterBottom>
                        Collection Not Found
                    </Typography>
                    <Typography>
                        The collection you're looking for doesn't exist or you don't have
                        permission to view it.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box id="contentArea" sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 12, sm: 8, md: 6 } }}>
                <HelpHint
                    title="Collection details"
                    hints={[
                        "Edit title, tier, and notes inline",
                        "View the active month for monthly distributions",
                        "Use the action buttons to send or delete",
                    ]}
                />

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 3,
                                mb: 3,
                                backgroundColor: (theme) => theme.palette.background.paper,
                                border: (theme) => `2px solid ${theme.palette.primary.main}`,
                                borderRadius: 2
                            }}
                        >
                            {/* Folder Path & Move Action */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                <Breadcrumbs aria-label="Collection folder path" separator="›">
                                    <Typography variant="body2" color="text.secondary">
                                        {this.state.folderPath && this.state.folderPath.length > 0
                                            ? this.state.folderPath.join(' / ')
                                            : 'Collections'}
                                    </Typography>
                                </Breadcrumbs>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => this.setState({ isMoveDialogOpen: true })}
                                    startIcon={<DriveFileMoveOutlinedIcon fontSize="small" />}
                                    sx={{ textTransform: 'none', py: 0, minHeight: 0 }}
                                    aria-label="Move collection to another folder"
                                >
                                    Move
                                </Button>
                            </Box>
                            <Typography variant="h4" component="h1" sx={{
                                fontWeight: 600, textAlign: 'center',
                                color: (theme) => theme.palette.primary.main,
                            }}>
                                {this.state.editName ? (
                                    <form onSubmit={this.editNameSubmit}>
                                        <TextField
                                            fullWidth
                                            value={this.state.collectionName}
                                            onChange={this.handleEditName}
                                            disabled={this.state.disableName}
                                            autoFocus
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: (theme) => theme.palette.primary.main,
                                                    '& fieldset': {
                                                        borderColor: (theme) => theme.palette.primary.main,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: (theme) => theme.palette.primary.dark,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: (theme) => theme.palette.primary.main,
                                                    },
                                                },
                                            }}
                                            onBlur={() =>
                                                this.setState({
                                                    editName: false,
                                                    collectionName: this.state.oldName,
                                                })
                                            }
                                        />
                                    </form>
                                ) : (
                                    <span
                                        id="tour-1"
                                        onClick={this.startEditName}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {this.state.collectionName || "Untitled Collection"}
                                    </span>
                                )}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                            <input
                                type="file"
                                multiple
                                accept=".jpg,.jpeg,.png,.bmp,.webp,.tiff,.mp4,.mov,.avi,.webm,.mkv,image/jpeg,image/png,image/bmp,image/webp,image/tiff,video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska"
                                onChange={this.onChange}
                                style={{ display: "none" }}
                                id="uploadButton"
                            />

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<FontAwesomeIcon icon={faPlus} />}
                                onClick={this.clickUpload}
                                id="addButton"
                                disabled={this.state.isUploading}
                            >
                                Add Media
                            </Button>

                            <Box id="tour-2" sx={{ display: 'inline-block' }}>
                                <TierDropdownPill
                                    defaultValue={this.state.tier}
                                    action={this.handleTierChange}
                                    token={this.props.token}
                                />
                            </Box>

                            {this.state.monthlyEnabled === true && (
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        views={["year", "month"]}
                                        label="Active Month"
                                        value={this.state.monthValue ? Moment(this.state.monthValue) : null}
                                        onChange={(value) => {
                                            if (value && value.isValid && value.isValid()) {
                                                const year = value.year();
                                                const month = value.month(); // 0-based
                                                const selectedDate = new Date(year, month, 15); // Local date for the 15th
                                                this.setState({ monthValue: selectedDate });
                                            } else {
                                                this.setState({ monthValue: null });
                                            }
                                        }}
                                        onAccept={(value) => {
                                            if (value && value.isValid && value.isValid()) {
                                                const year = value.year();
                                                const month = value.month(); // 0-based
                                                const selectedDate = new Date(year, month, 15);
                                                this.handleChangeActiveMonthWithValue(selectedDate);
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                id: "tour-3"
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            )}

                            <Button
                                variant="outlined"
                                onClick={this.toggleSortMode}
                                id="tour-4"
                                disabled={this.state.media.length === 0}
                            >
                                {this.state.sortDesc}
                            </Button>

                            <Button
                                variant="outlined"
                                color="success"
                                startIcon={<FontAwesomeIcon icon={faDownload} />}
                                onClick={this.downloadCollection}
                                disabled={
                                    !this.state.allowDownload ||
                                    this.state.isRequesting ||
                                    this.state.media.length === 0
                                }
                                id="tour-5"
                            >
                                {this.state.isRequesting ? "Preparing..." : "Download"}
                            </Button>

                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<FontAwesomeIcon icon={faTrash} />}
                                onClick={this.deleteCollection}
                                id="tour-6"
                            >
                                Delete
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Collapse in={this.state.collapseProgress}>
                            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    Uploading {this.state.filesToSend} files...
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={this.state.percent}
                                    style={{ marginBottom: "8px" }}
                                />
                                <Typography variant="caption">
                                    {Math.round(this.state.percent)}% complete
                                </Typography>
                            </Paper>
                        </Collapse>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 2,
                                position: 'relative'
                            }}
                            onDragEnter={this.handleMediaAreaDragEnter}
                            onDragLeave={this.handleMediaAreaDragLeave}
                            onDragOver={this.handleMediaAreaDragOver}
                            onDrop={this.handleMediaAreaDrop}
                        >
                            {this.state.dragging && (
                                <div
                                    style={{
                                        position: "fixed",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: "rgba(0,0,0,0.5)",
                                        zIndex: 1000,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        pointerEvents: "none",
                                    }}
                                >
                                    <Paper
                                        elevation={8}
                                        sx={{
                                            padding: "20px",
                                            textAlign: "center",
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(30,30,30,0.95)'
                                                    : 'rgba(255,255,255,0.95)',
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <Typography variant="h5" gutterBottom>
                                            Drop files here to upload
                                        </Typography>
                                        <FontAwesomeIcon
                                            icon={faPlus}
                                            size="3x"
                                            style={{ color: "var(--primary-main, #1976d2)" }}
                                        />
                                    </Paper>
                                </div>
                            )}

                            <Typography variant="h6" gutterBottom id="tour-10">
                                Media ({this.state.media.length})
                            </Typography>

                            {!this.state.mediaLoaded ? (
                                <Box
                                    textAlign="center"
                                    p={4}
                                    sx={{
                                        border: (theme) => `2px dashed ${theme.palette.divider}`,
                                        borderRadius: "8px",
                                        backgroundColor: (theme) => theme.palette.background.default,
                                    }}
                                >
                                    <CircularProgress size={40} style={{ marginBottom: "16px" }} />
                                    <Typography variant="body1" color="text.secondary">
                                        Loading media...
                                    </Typography>
                                </Box>
                            ) : this.state.media.length === 0 ? (
                                <Box
                                    textAlign="center"
                                    p={4}
                                    sx={{
                                        border: (theme) => `2px dashed ${theme.palette.divider}`,
                                        borderRadius: "8px",
                                        backgroundColor: (theme) => theme.palette.background.default,
                                    }}
                                >
                                    <Typography variant="body1" color="text.secondary">
                                        No media uploaded yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Click "Add Media" or drag files here to get started
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {this.state.sortMode ? (
                                        <DndContext
                                            sensors={this.props.sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={this.handleDragEnd}
                                        >
                                            <SortableContext
                                                items={this.state.media.map((m) => m.id)}
                                                strategy={rectSortingStrategy}
                                            >
                                                <Grid container spacing={1}>
                                                    {this.state.media.map((mediaItem, index) => (
                                                        <SortableMedia
                                                            key={mediaItem.id}
                                                            id={mediaItem.id}
                                                            data={mediaItem}
                                                            sortMode={this.state.sortMode}
                                                            onClick={() => {
                                                                console.log("Clicking media:", mediaItem, "index:", index, "status:", mediaItem.status, "sortMode:", this.state.sortMode);
                                                                if (!this.state.sortMode && (mediaItem.status === "SUCCESS" || mediaItem.status === "FAILED")) {
                                                                    // SS-461: FAILED items open the modal in recoveryMode (Redrive + Delete).
                                                                    console.log("Setting modal state:", {
                                                                        showModal: true,
                                                                        currentImageIndex: index,
                                                                        currentImageStatus: mediaItem.status,
                                                                        totalImages: this.state.media.length
                                                                    });
                                                                    this.setState({
                                                                        showModal: true,
                                                                        currentImageIndex: index,
                                                                        currentImageStatus: mediaItem.status
                                                                    });
                                                                } else if (this.state.sortMode) {
                                                                    console.log("In sort mode, not opening modal");
                                                                } else {
                                                                    toast.warn("This media is still processing. Please check back later.");
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Grid>
                                            </SortableContext>
                                        </DndContext>
                                    ) : (
                                        <Grid container spacing={1}>
                                            {this.state.media.map((mediaItem, index) => (
                                                <SortableMedia
                                                    key={mediaItem.id}
                                                    id={mediaItem.id}
                                                    data={mediaItem}
                                                    sortMode={this.state.sortMode}
                                                    onClick={() => {
                                                        console.log("Clicking media:", mediaItem, "index:", index, "status:", mediaItem.status, "sortMode:", this.state.sortMode);
                                                        if (!this.state.sortMode && (mediaItem.status === "SUCCESS" || mediaItem.status === "FAILED")) {
                                                            console.log("Setting modal state:", {
                                                                showModal: true,
                                                                currentImageIndex: index,
                                                                currentImageStatus: mediaItem.status,
                                                                totalImages: this.state.media.length
                                                            });
                                                            this.setState({
                                                                showModal: true,
                                                                currentImageIndex: index,
                                                                currentImageStatus: mediaItem.status
                                                            });
                                                        } else if (this.state.sortMode) {
                                                            console.log("In sort mode, not opening modal");
                                                        } else {
                                                            toast.warn("This media is still processing. Please check back later.");
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ pr: { md: 2 } }}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" gutterBottom id="tour-7">
                                Status
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    padding: "8px 16px",
                                    backgroundColor: this.state.mediaLoaded ? this.state.statusColor : 'action.hover',
                                    borderRadius: "4px",
                                    textAlign: "center",
                                    color: this.state.mediaLoaded ? (this.state.statusColor === "#DDDDDD" || this.state.statusColor === "#6c757d" ? "#000" : "#fff") : 'text.secondary',
                                    fontWeight: "500",
                                }}
                            >
                                {this.state.mediaLoaded ? this.state.setStatus : "Loading..."}
                            </Typography>
                            {this.state.expectedMediaCount > 0 && this.state.mediaLoaded && (
                                <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        textAlign: "center",
                                        mt: 1,
                                        color: 'text.secondary',
                                    }}
                                >
                                    {this.state.successMediaCount} of {this.state.expectedMediaCount} media processed
                                    {this.state.processingMediaCount > 0 && (
                                        <span> ({this.state.processingMediaCount} processing)</span>
                                    )}
                                    {this.state.failedMediaCount > 0 && (
                                        <span style={{ color: "#dc3545" }}> ({this.state.failedMediaCount} failed)</span>
                                    )}
                                </Typography>
                            )}
                        </Paper>

                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" gutterBottom id="tour-8">
                                Notes
                            </Typography>
                            {this.state.editNote ? (
                                <form onSubmit={this.editNoteSubmit}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={this.state.notes}
                                        onChange={this.handleEditNote}
                                        onKeyPress={this.handleNotesKeyPress}
                                        disabled={this.state.disableNotes}
                                        autoFocus
                                        placeholder="Press Enter to save, Shift+Enter for new line"
                                        onBlur={() =>
                                            this.setState({
                                                editNote: false,
                                                notes: this.state.oldNotes,
                                            })
                                        }
                                    />
                                </form>
                            ) : (
                                <Typography
                                    variant="body1"
                                    onClick={this.startEditNote}
                                    sx={{
                                        cursor: "pointer",
                                        minHeight: "40px",
                                        p: 1,
                                        border: "1px solid transparent",
                                        borderRadius: "4px",
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    {this.state.notes || "Click to add notes..."}
                                </Typography>
                            )}
                        </Paper>

                        <Paper elevation={1} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom id="tour-9">
                                Copyright
                            </Typography>
                            {this.state.editCopyright ? (
                                <form onSubmit={this.editCopyrightSubmit}>
                                    <TextField
                                        fullWidth
                                        value={this.state.copyright}
                                        onChange={this.handleCopyright}
                                        disabled={this.state.disableCopyright}
                                        autoFocus
                                        onBlur={() =>
                                            this.setState({
                                                editCopyright: false,
                                                copyright: this.state.oldCopyright,
                                            })
                                        }
                                    />
                                </form>
                            ) : (
                                <Typography
                                    variant="body1"
                                    onClick={this.startEditCopyright}
                                    sx={{
                                        cursor: "pointer",
                                        minHeight: "24px",
                                        p: 1,
                                        border: "1px solid transparent",
                                        borderRadius: "4px",
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    {this.state.copyright || "Click to add copyright..."}
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                <ImagePreviewModal
                    open={this.state.showModal}
                    onClose={this.closeModal}
                    media={this.state.media}
                    initialIndex={this.state.currentImageIndex}
                    collectionDetails={{
                        collectionName: this.state.collectionName,
                        createdAt: this.state.createdAt,
                    }}
                    awsCredentials={this.props.AWS?.config?.credentials}
                    userId={this.state.collectionOwnerId}
                    collectionId={this.state.collectionId}
                    versionTime={this.state.versionTime}
                    token={this.props.token}
                    onMediaDeleted={(deletedId) => {
                        // Decrement expected counts and refresh images
                        this.decrementCountsAfterDelete(deletedId);
                        this.initGetMedia = false;
                        this.setState({ media: [] }, () => this.getMedia());
                    }}
                />

                {this.state.isMoveDialogOpen && (
                    <CollectionFolderMoveDialog
                        open={this.state.isMoveDialogOpen}
                        collectionId={this.state.collectionId}
                        currentFolderPath={this.state.folderPath}
                        collections={this.state.allCollections}
                        token={this.props.token}
                        onClose={() => this.setState({ isMoveDialogOpen: false })}
                        onMoved={(newPath) => {
                            this.setState({
                                isMoveDialogOpen: false,
                                folderPath: newPath,
                                snackbar: { open: true, message: 'Collection moved successfully', severity: 'success' }
                            });
                        }}
                    />
                )}

                <Snackbar
                    open={this.state.snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => this.setState({ snackbar: { ...this.state.snackbar, open: false } })}
                >
                    <Alert severity={this.state.snackbar.severity} onClose={() => this.setState({ snackbar: { ...this.state.snackbar, open: false } })}>
                        {this.state.snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }
}

function CollectionDetailsWrapper(props) {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );
    return (
        <CollectionDetails
            {...props}
            navigate={navigate}
            match={{ params }}
            location={location}
            sensors={sensors}
        />
    );
}

export default CollectionDetailsWrapper;
